import React, { useState } from 'react';
import { Dialog } from '../../components/ui/Dialog';
import {
  User,
  Calendar,
  CreditCard,
  Mail,
  Phone,
  Building2,
  Globe,
  Clock,
  DollarSign,
  Briefcase,
  MapPin
} from 'lucide-react';
import { CustomerDetailResponse } from '../../services/customer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { UserRole } from '../../types/auth';
import { useAppSelector } from '../../store/hooks/reduxHooks';

interface CustomerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerData: CustomerDetailResponse | null;
  isLoading: boolean;
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  isOpen,
  onClose,
  customerData,
  isLoading
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'logs'>('details');
  const { user } = useAppSelector(state => state.auth);
  const isCleverUser = user?.role === UserRole.CLEVERPE_USER;

  if (!customerData && !isLoading) return null;

  const result: any = customerData?.result;
  const customer = result?.customer;
  const logs = result?.logs || [];
  const merchant = result?.merchant;
  // API returns the partner as `gatewayPartner`; fall back to `partner` for older responses
  const partner = result?.gatewayPartner ?? result?.partner;
  const platform = result?.platform;
  const addresses: any[] = customer?.addresses ?? [];

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format platform for display
  const formatPlatform = (platform?: string) => {
    if (!platform) return 'N/A';

    switch (platform.toLowerCase()) {
      case 'merchant':
        return 'Merchant';
      case 'gateway-partner':
      case 'gateway':
        return 'Gateway';
      case 'customer':
        return 'Website';
      default:
        return platform;
    }
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender?: string | null) => {
    if (!lender) return <span className="text-gray-500">N/A</span>;

    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'general':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      case 'caprion':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-2 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  // Get employment type label
  const getEmploymentTypeLabel = (type?: number) => {
    switch (type) {
      case 1: return 'Salaried';
      case 2: return 'Self-employed';
      case 3: return 'Business Owner';
      default: return 'N/A';
    }
  };

  // Format currency
  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Customer Details"
      size="lg"
    >
      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <div className="w-10 h-10 border-4 border-gray-200 rounded-full border-t-blue-500 animate-spin"></div>
        </div>
      ) : customer ? (
        <div className="p-4">
          {/* Customer Header */}
          <div className="flex items-center pb-4 mb-4">
            <div className="flex items-center justify-center w-12 h-12 mr-3 text-xl font-bold text-white rounded-full bg-gradient-to-r from-blue-500 to-indigo-600">
              {customer.name ? customer.name.charAt(0) : customer.mobileNo.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-800 truncate">{customer.name || 'Customer'}</h2>
              <div className="flex flex-wrap items-center mt-1 space-x-2 text-sm">
                <div className="flex items-center text-gray-500">
                  <Phone size={14} className="mr-1" />
                  {customer.mobileNo}
                </div>
                {customer.email && (
                  <div className="flex items-center text-gray-500 max-w-[200px]">
                    <Mail size={14} className="flex-shrink-0 mr-1" />
                    <span className="truncate" title={customer.email}>{customer.email}</span>
                  </div>
                )}
                {!isCleverUser && platform && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {formatPlatform(platform)}
                  </span>
                )}
                {!isCleverUser && customer.preApprovedMaxMatchLender && (
                  <div className="flex items-center">
                    {renderLenderBadge(customer.preApprovedMaxMatchLender)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab as any}>
            <TabsList className="mb-6">
              <TabsTrigger value="details">Customer Information</TabsTrigger>

              {!isCleverUser && (
                <TabsTrigger value="logs">Pre-approval Logs</TabsTrigger>
              )}
            </TabsList>

            {/* Customer Information Tab */}
            <TabsContent value="details" className="space-y-4">
              {/* Customer Basic Info */}
              <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                  <div className="p-2 mr-3 bg-blue-100 rounded-full">
                    <User className="text-blue-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Name</p>
                    <p className="font-medium">{customer.name || 'N/A'}</p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                    <div className="flex items-center">
                      <Phone size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{customer.mobileNo}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Email</p>
                    <div className="flex items-center">
                      <Mail size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium truncate" title={customer.email || 'N/A'}>{customer.email || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">PAN Number</p>
                    <div className="flex items-center">
                      <CreditCard size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{customer.pan || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Date of Birth</p>
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{formatDate(customer.dob)}</p>
                    </div>
                  </div>

                  {!isCleverUser && (
                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Platform</p>
                      <div className="flex items-center">
                        <Globe size={14} className="mr-1 text-gray-500" />
                        <p className="font-medium">{formatPlatform(platform)}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Employment Type</p>
                    <div className="flex items-center">
                      <Briefcase size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{getEmploymentTypeLabel(customer.employmentType)}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Company Name</p>
                    <div className="flex items-center">
                      <Building2 size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{customer.companyName || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Monthly Income</p>
                    <div className="flex items-center">
                      <DollarSign size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{formatCurrency(customer.monthlyIncome)}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Pre-approved Amount</p>
                    <div className="flex items-center">
                      <DollarSign size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{formatCurrency(customer.preApprovedAmount)}</p>
                    </div>
                  </div>

                  {!isCleverUser && (
                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Match Lender</p>
                      <div className="flex items-center">
                        {renderLenderBadge(customer.preApprovedMaxMatchLender)}
                      </div>
                    </div>
                  )}

                  {!isCleverUser && (
                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Customer From</p>
                      <p className="font-medium">{customer.customerFrom || 'N/A'}</p>
                    </div>
                  )}

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Created At</p>
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{formatDate(customer.createdAt)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Details */}
              {addresses.length > 0 && (
                <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                    <div className="p-2 mr-3 bg-orange-100 rounded-full">
                      <MapPin className="text-orange-600" size={18} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Address Details{addresses.length > 1 ? ` (${addresses.length})` : ''}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {addresses.map((addr, idx) => (
                      <div
                        key={idx}
                        className={idx > 0 ? 'pt-4 border-t border-gray-100' : ''}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                          <div className="md:col-span-2 lg:col-span-3">
                            <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Full Address</p>
                            <p className="font-medium">{addr.fullAddress || 'N/A'}</p>
                          </div>

                          <div>
                            <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Area</p>
                            <p className="font-medium">{addr.area || 'N/A'}</p>
                          </div>

                          <div>
                            <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">City</p>
                            <p className="font-medium">{addr.city || 'N/A'}</p>
                          </div>

                          <div>
                            <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">State</p>
                            <p className="font-medium">{addr.state || 'N/A'}</p>
                          </div>

                          <div>
                            <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Pincode</p>
                            <p className="font-medium">{addr.pincode || 'N/A'}</p>
                          </div>

                          <div>
                            <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Ownership</p>
                            <span className="px-2 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                              {addr.ownershipType || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Merchant Info */}
              {merchant && (
                <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                    <div className="p-2 mr-3 bg-purple-100 rounded-full">
                      <Building2 className="text-purple-600" size={18} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Merchant Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Merchant Name</p>
                      <p className="font-medium">{merchant.name || 'N/A'}</p>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                      <div className="flex items-center">
                        <Phone size={14} className="mr-1 text-gray-500" />
                        <p className="font-medium">{merchant.mobileNo || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Email</p>
                      <div className="flex items-center">
                        <Mail size={14} className="mr-1 text-gray-500" />
                        <p className="font-medium truncate" title={merchant.email || 'N/A'}>{merchant.email || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Group</p>
                      <div className="flex items-center">
                        <Building2 size={14} className="mr-1 text-gray-500" />
                        <p className="font-medium">{merchant.afpId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Partner Info */}
              {!isCleverUser && partner && (
                <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                    <div className="p-2 mr-3 bg-green-100 rounded-full">
                      <Building2 className="text-green-600" size={18} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Partner Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Partner Name</p>
                      <p className="font-medium">{partner.name || 'N/A'}</p>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                      <div className="flex items-center">
                        <Phone size={14} className="mr-1 text-gray-500" />
                        <p className="font-medium">{partner.mobileNo || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Email</p>
                      <div className="flex items-center">
                        <Mail size={14} className="mr-1 text-gray-500" />
                        <p className="font-medium truncate" title={partner.email || 'N/A'}>{partner.email || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Lender</p>
                      <div className="flex items-center">
                        {renderLenderBadge(partner.lender)}
                      </div>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Type</p>
                      <div className="flex items-center space-x-2">
                        {partner.isEv && (
                          <span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                            EV
                          </span>
                        )}
                        {partner.isBrand && (
                          <span className="px-2 py-1 text-xs font-medium text-purple-800 bg-purple-100 rounded-full">
                            Brand
                          </span>
                        )}
                        {!partner.isEv && !partner.isBrand && (
                          <span className="text-gray-500">Standard</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Logs Tab */}
            <TabsContent value="logs">
              <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Sr No
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Timestamp
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Cashe
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Zype
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.map((log: any) => (
                        <tr key={log.srNo}>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {log.srNo}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                            <div className="flex items-center">
                              <Clock size={14} className="mr-1 text-gray-400" />
                              {log.timestamp}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.cashe ? (
                              <div>
                                <div className="flex items-center">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.cashe.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                    }`}>
                                    {log.cashe.status}
                                  </span>
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                  {formatCurrency(log.cashe.amount)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500">N/A</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.zype ? (
                              <div>
                                {log.zype.status ? (
                                  <div className="flex items-center">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.zype.status === 'ACCEPT'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                      }`}>
                                      {log.zype.status}
                                    </span>
                                  </div>
                                ) : log.zype.error ? (
                                  <div className="text-xs text-red-600">
                                    {log.zype.error}
                                  </div>
                                ) : null}

                                <div className="mt-1 text-xs text-gray-500">
                                  {log.zype.message && <div>{log.zype.message}</div>}
                                  {log.zype.offer && <div>{formatCurrency(log.zype.offer)}</div>}
                                  {log.zype.timestamp && <div>{new Date(log.zype.timestamp).toLocaleString()}</div>}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}

                      {logs.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-sm text-center text-gray-500">
                            No logs available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">
          No customer data available
        </div>
      )}
    </Dialog>
  );
};

export default CustomerDetailModal;